import { useState } from "react";
import SweetAlert2 from "react-sweetalert2";
import axios from "axios";
import Card from "components/card";
import { Button } from "@chakra-ui/react";
import { Tooltip, IconButton } from "@mui/material";
import { MdOutlineContentCopy } from "react-icons/md";

const AdminUtils = () => {
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [swalProps, setSwalProps] = useState<any>({});

  // db_encrypt_helper state
  const [inputData, setInputData] = useState<string>("");
  const [result, setResult] = useState<any>(null);

  const handleClear = () => {
    setInputData("");
    setResult(null);
  };

  const handleDbEncryptHelper = async (encrypt: boolean) => {
    if (!inputData.trim()) {
      setSwalProps({
        show: true,
        title: "Error",
        text: "Please enter input data",
        icon: "error",
      });
      return;
    }

    setLoadingAction(encrypt ? "encrypt" : "decrypt");
    setResult(null);

    try {
      let processedInput = inputData;
      if (encrypt) {
        try {
          processedInput = JSON.stringify(JSON.parse(inputData));
        } catch {
          // not JSON, send as-is
        }
      }

      const response = await axios.post(
        process.env.REACT_APP_API_URL + "/api/superadmin/admin/action/utils",
        {
          func_name: "db_encrypt_helper",
          data: {
            is_encrypt: encrypt,
            input_data: processedInput,
          },
        }
      );

      if (response.data) {
        setResult(response.data.payload);
      }
    } catch (error: any) {
      console.error("Error calling db_encrypt_helper:", error);
      setSwalProps({
        show: true,
        title: "Error",
        text: error.response?.data?.message || "Something went wrong",
        icon: "error",
      });
    } finally {
      setLoadingAction(null);
    }
  };

  return (
    <>
      <div className="mt-3 text-sm">
        <Card extra="p-6">
          <div className="mb-4">
            <textarea
              value={inputData}
              onChange={(e) => setInputData(e.target.value)}
              placeholder="Enter data to encrypt/decrypt..."
              className="max-h-96 min-h-[20rem] w-full overflow-auto break-all rounded-lg border border-gray-300 p-3 text-sm focus:border-brand-500 focus:outline-none"
            />
          </div>

          <div className="mb-4 flex items-center justify-center gap-2">
            <Button
              onClick={() => handleDbEncryptHelper(true)}
              isDisabled={!!loadingAction}
              isLoading={loadingAction === "encrypt"}
              loadingText="Encrypting..."
              style={{ minWidth: "200px" }}
              className="btn-primary rounded-md py-2 text-sm font-medium text-white shadow-sm"
            >
              Encrypt
            </Button>
            <Button
              onClick={() => handleDbEncryptHelper(false)}
              isDisabled={!!loadingAction}
              isLoading={loadingAction === "decrypt"}
              loadingText="Decrypting..."
              style={{ minWidth: "200px" }}
              className="btn-primary rounded-md py-2 text-sm font-medium text-white shadow-sm"
            >
              Decrypt
            </Button>
            <Button
              onClick={handleClear}
              isDisabled={!!loadingAction}
              variant="outline"
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-navy-700 shadow-sm hover:bg-gray-50"
            >
              Clear
            </Button>
          </div>

          <div>
            <div className="mb-2 flex items-center gap-2">
              {result && (
                <Tooltip title="Copy Result" arrow placement="top">
                  <IconButton
                    size="small"
                    onClick={() => {
                      let text = typeof result === "string" ? result : JSON.stringify(result, null, 2);
                      if (typeof result === "string") {
                        try {
                          text = JSON.stringify(JSON.parse(result), null, 2);
                        } catch {
                          // not JSON, copy as-is
                        }
                      }
                      navigator.clipboard.writeText(text);
                    }}
                  >
                    <MdOutlineContentCopy className="h-5 w-5 text-navy-700" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
            <textarea
              readOnly
              value={
                result
                  ? (() => {
                      if (typeof result === "string") {
                        try {
                          return JSON.stringify(JSON.parse(result), null, 2);
                        } catch {
                          return result;
                        }
                      }
                      return JSON.stringify(result, null, 2);
                    })()
                  : ""
              }
              placeholder="Result"
              className="max-h-96 min-h-[20rem] w-full overflow-auto break-all rounded-lg border border-gray-300 p-3 text-sm focus:border-brand-500 focus:outline-none"
            />
          </div>
        </Card>
      </div>

      <SweetAlert2
        {...swalProps}
        onConfirm={() => setSwalProps({ show: false })}
        customClass={{
          actions: "custom-swal2-popup-action",
          title: "custom-swal2-popup-title",
          confirmButton:
            "custom-swal2-popup-action custom-swal2-popup-action-confirm",
          cancelButton:
            "custom-swal2-popup-action custom-swal2-popup-action-cancel",
        }}
      />
    </>
  );
};

export default AdminUtils;
